import { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../context/UserContext';
import PageContainer from '../../components/page-container/PageContainer';
import ProjectCard from '../../components/project-card/ProjectCard';
import DataTable from '../../components/data-table/DataTable';
import { CardSkeleton, TableSkeleton } from '../../components/skeleton';

import ProjectApi from '../../services/ProjectApi';
import DocumentApi from '../../services/DocumentApi';

export default function ProjectPage() {
  const { t } = useTranslation();
  const projectApi = new ProjectApi();
  const documentApi = new DocumentApi();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [projects, setProjects] = useState([]);
  const [projectStats, setProjectStats] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastVisible, setLastVisible] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const PAGE_SIZE = 6;

  const getData = async (page = 1) => {
    try {
      const response = await projectApi.totalParticipantProjects(user.uid);
      if (!response.success) {
        console.error('Error fetching total projects:', response.error);
        return;
      }
      const totalCount = response.totalCount;
      setTotalPages(Math.ceil(totalCount / PAGE_SIZE));
      const response1 = await projectApi.getParticipantProjects(user.uid, lastVisible, PAGE_SIZE, page);
      setProjects(response1.projects);
      setLastVisible(response1.lastVisible);

      // Fetch stats for each project
      const statsPromises = response1.projects.map(async (project) => {
        const statsRes = await documentApi.getDocumentStats(project.id);
        return { projectId: project.id, stats: statsRes.success ? statsRes.stats : null };
      });
      const statsResults = await Promise.all(statsPromises);
      const statsMap = {};
      statsResults.forEach(({ projectId, stats }) => {
        statsMap[projectId] = stats;
      });
      setProjectStats(statsMap);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
    setLoading(false);
    setPaginationLoading(false);
  };

  useEffect(() => {
    if (user?.uid) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handlePageChange = (event, value) => {
    setPaginationLoading(true);
    setCurrentPage(value);
    getData(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await projectApi.createProject({
        name,
        description,
        ownerId: user.uid,
        owner: user.user || null,
        companyId: user.companyId || null,
        company: user.company || null,
      });

      setSnackbar({ open: true, message: t('projects.create_success'), severity: 'success' });
      setOpenDialog(false);
      setName('');
      setDescription('');
      getData();
    } catch (error) {
      console.error('Error creating project:', error);
      setSnackbar({ open: true, message: t('projects.create_error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = useMemo(() => {
    if (!searchTerm) return projects;
    const search = searchTerm.toLowerCase();
    return projects.filter(
      (project) =>
        project.name?.toLowerCase().includes(search) ||
        project.description?.toLowerCase().includes(search)
    );
  }, [projects, searchTerm]);

  const tableColumns = [
    {
      field: 'name',
      headerName: t('projects.name'),
      searchable: true,
      minWidth: 200,
    },
    {
      field: 'description',
      headerName: t('projects.description'),
      minWidth: 300,
      renderCell: (row) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 300,
          }}
        >
          {row.description || '-'}
        </Typography>
      ),
    },
    {
      field: 'documents',
      headerName: t('projects.documents'),
      minWidth: 100,
      renderCell: (row) => projectStats[row.id]?.total || 0,
    },
    {
      field: 'createdAt',
      headerName: t('projects.created'),
      minWidth: 120,
      renderCell: (row) => row.createdAt?.toDate?.()?.toLocaleDateString() || '-',
    },
  ];

  if (loading) {
    return (
      <PageContainer>
        {/* Header skeleton */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Skeleton variant="text" width={250} height={48} />
          <Skeleton variant="rounded" width={150} height={40} />
        </Box>

        {/* Search bar skeleton */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            gap: 2,
          }}
        >
          <Skeleton variant="rounded" width={250} height={40} />
          <Skeleton variant="rounded" width={80} height={40} />
        </Box>

        {/* Projects grid skeleton */}
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <CardSkeleton showStats />
            </Grid>
          ))}
        </Grid>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        <Typography variant="h3">{t('projects.my_projects')}</Typography>
        {user?.role === 'producer' && (
          <Button variant="contained" onClick={() => setOpenDialog(true)}>
            + {t('projects.new_project')}
          </Button>
        )}
      </Box>

      {/* Search and View Toggle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <TextField
          placeholder={t('projects.search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="grid">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="list">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Projects Display */}
      {paginationLoading ? (
        viewMode === 'grid' ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <CardSkeleton showStats />
              </Grid>
            ))}
          </Grid>
        ) : (
          <TableSkeleton rows={6} columns={4} showSearch={false} />
        )
      ) : filteredProjects.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography color="text.secondary">
            {searchTerm ? t('projects.no_results') : t('projects.no_projects')}
          </Typography>
        </Box>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredProjects.map((project) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={project.id}>
              <ProjectCard
                project={project}
                stats={projectStats[project.id]}
                onClick={() => navigate(`/project/${project.id}`)}
                showStats
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <DataTable
          columns={tableColumns}
          data={filteredProjects}
          searchable={false}
          pagination={false}
          onRowClick={(row) => navigate(`/project/${row.id}`)}
          emptyMessage={t('projects.no_projects')}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
        </Box>
      )}

      {/* Create Project Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('projects.create_project')}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('projects.project_name')}
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={t('projects.description')}
              variant="outlined"
              multiline
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ mb: 2 }}
            />
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="secondary">
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={24} /> : t('common.create')}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
